import React, { useCallback, useState } from 'react';
import api from '../../services/api';
import InterventionModal from '../InterventionModal';
import './DossierPlanification.css';

// ═══════════════════════════════════════════════════════════════════════════
// Colonne « Planification » partagée.
//
// Sept écrans affichent la date à laquelle un véhicule est attendu à l'atelier
// (Gestion Parking, OR en cours, Suivi Atelier, Véhicules courtoisie, Litiges,
// Réception VIP, Pièces atelier). Le geste attendu est partout le même : lire
// la date, la couleur du statut, et ouvrir le dossier — SANS quitter l'écran
// où l'on travaille. La logique vivait en double (Parking et OR en cours) ;
// elle est ici en un seul endroit avant que la duplication n'atteigne sept.
//
// Ce que fournit ce module :
//   · <PlanificationCell>   la cellule : date colorée, cliquable ou inerte ;
//   · useDossierPlanning()  l'état, le chargement et la modale à rendre.
//
// Les référentiels (statuts, travaux, staff) sont chargés au PREMIER clic et
// conservés : les rapatrier au montage ferait payer trois requêtes à tous ceux
// qui ne cliqueront jamais sur une date.
// ═══════════════════════════════════════════════════════════════════════════

const formatJour = (v) => {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  const [a, m, j] = s.split('-');
  return (a && m && j) ? `${j}/${m}/${a}` : s;
};

/**
 * Cellule de la colonne Planification.
 * `dossier` accepte indifféremment les noms de champs déjà en place côté API :
 * date_planification, statut_atelier_* et planning_intervention_id.
 */
export function PlanificationCell({ dossier, onOuvrir, cliquable = true }) {
  const date = dossier?.date_planification;
  if (!date) return <span className="dpl-vide">—</span>;

  const couleur = dossier.statut_atelier_couleur || null;
  const texte = couleur ? (dossier.statut_atelier_text_color || '#000000') : null;
  const statut = dossier.statut_atelier_nom || '';
  const id = dossier.planning_intervention_id;

  // Sans dossier joignable (ou sans droit sur le Planning), la date reste
  // affichée aux couleurs du statut : l'information vaut d'être lue même
  // quand on ne peut pas l'ouvrir.
  if (!id || !cliquable) {
    return (
      <span
        className={`dpl-date${couleur ? '' : ' dpl-date--nue'}`}
        title={statut}
        style={couleur ? { background: couleur, color: texte } : undefined}
      >{formatJour(date)}</span>
    );
  }

  return (
    <button
      type="button"
      className={`dpl-date dpl-date--btn${couleur ? '' : ' dpl-date--nue'}`}
      title={`Ouvrir le dossier${statut ? ` — ${statut}` : ''}`}
      style={couleur ? { background: couleur, color: texte } : undefined}
      onClick={(e) => { e.stopPropagation(); onOuvrir(dossier); }}
    >{formatJour(date)}</button>
  );
}

/**
 * État + modale du dossier de planification.
 * @param {object}   user          utilisateur courant (prop de la page)
 * @param {boolean}  editMode      mode édition global du Dashboard
 * @param {function} onEnregistre  rappel après enregistrement/suppression
 */
export function useDossierPlanning({ user, editMode = false, onEnregistre } = {}) {
  const [dossier, setDossier] = useState(null);
  const [chargement, setChargement] = useState(null);
  const [refs, setRefs] = useState(null);

  const chargerRefs = useCallback(async () => {
    if (refs) return refs;
    // allSettled : un référentiel indisponible ne doit pas empêcher d'ouvrir
    // le dossier — la modale sait fonctionner avec des listes vides.
    const r = await Promise.allSettled([
      api.get('/referentiels/statuts-atelier'),
      api.get('/referentiels/travaux-types'),
      api.get('/planning/staff'),
    ]);
    const val = (i, d) => (r[i].status === 'fulfilled' ? r[i].value.data : d);
    const staff = val(2, []) || [];
    const suivant = {
      statutsAtelier: (val(0, { statuts_atelier: [] }).statuts_atelier) || [],
      travauxTypes: (val(1, { travaux_types: [] }).travaux_types) || [],
      mecaniciens: staff.filter(s => s.role === 'mecanicien' && s.actif !== false),
      receptionnaires: staff.filter(s => s.role === 'receptionnaire' && s.actif !== false),
    };
    setRefs(suivant);
    return suivant;
  }, [refs]);

  const ouvrir = useCallback(async (ligne) => {
    const id = ligne?.planning_intervention_id;
    if (!id || chargement) return;
    setChargement(id);
    try {
      const [, r] = await Promise.all([chargerRefs(), api.get(`/planning/interventions/${id}`)]);
      const intervention = r.data?.intervention;
      if (!intervention) { alert("Ce dossier n'est plus au planning."); return; }
      setDossier(intervention);
    } catch (e) {
      console.error('[planification/ouvrir]', e);
      alert("Impossible d'ouvrir le dossier.");
    } finally {
      setChargement(null);
    }
  }, [chargement, chargerRefs]);

  // Bascule explicite vers la grille : l'id est déposé, le Planning le
  // consomme au montage, se positionne sur la journée du dossier et rouvre
  // la modale (même mécanique différée que sa recherche globale).
  const allerAuPlanning = useCallback((interventionId) => {
    if (!interventionId) return;
    try { sessionStorage.setItem('planning-open-intervention', String(interventionId)); }
    catch (e) { /* stockage indisponible : la page s'ouvrira sans modale */ }
    window.dispatchEvent(new CustomEvent('crm-navigate', { detail: { page: 'planning' } }));
  }, []);

  const fermer = useCallback((rafraichir) => {
    setDossier(null);
    if (rafraichir && onEnregistre) onEnregistre();
  }, [onEnregistre]);

  const modale = (dossier && refs) ? (
    <InterventionModal
      show
      intervention={dossier}
      user={user}
      editMode={editMode}
      statutsAtelier={refs.statutsAtelier}
      travauxTypes={refs.travauxTypes}
      mecaniciens={refs.mecaniciens}
      mecaniciensTous={refs.mecaniciens}
      receptionnaires={refs.receptionnaires}
      onOuvrirPlanning={allerAuPlanning}
      onClose={() => fermer(false)}
      onSaved={() => fermer(true)}
      onDelete={() => fermer(true)}
    />
  ) : null;

  return { ouvrir, modale, chargement };
}
